import React, { useCallback, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { tripColor } from "../utils/truckActivity";

// Лента прогресса рейса в окне видео: «Рейс X | Рейс X+1 | Нет рейсов ...» едет под неподвижным
// маркером по времени кадра. Кладется в src/core/pages/CameraDetailPage/components/TripProgressStrip.jsx
//
// У каждого рейса и каждого промежутка без рейсов одинаковая ширина, поэтому лента движется
// с разной скоростью: длинный рейс проходит под маркером медленнее, а граница рейсов
// оказывается под маркером ровно в момент смены рейса.
// Обновляется напрямую через DOM по playheadStore - без рендера страницы.
// Двойной клик по рейсу (или по «Нет рейсов») - перемотка видео к его началу (onSeek).

// Ширина одного рейса на ленте
const TRIP_PIECE_PX = 150;
const NO_TRIP_COLOR = "#e9ecef";

// Индекс элемента, в который попадает момент ms (элементы отсортированы и не пересекаются)
export const indexAt = (items, ms) => {
  let lo = 0;
  let hi = items.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (items[mid].end <= ms) lo = mid + 1;
    else hi = mid;
  }
  return lo < items.length && items[lo].start <= ms ? lo : -1;
};

// Рейсы по порядку, между ними и по краям периода - «Нет рейсов»
const buildTripPieces = (trips, range) => {
  const sorted = [...trips].sort((a, b) => a.start - b.start);
  const pieces = [];
  let cursor = range ? range.start : sorted.length ? sorted[0].start : null;
  const pushGap = (start, end) => {
    if (cursor != null && end - start > 1000) {
      pieces.push({ key: `none_${start}`, none: true, start, end, label: "Нет рейсов" });
    }
  };
  sorted.forEach((trip, idx) => {
    if (cursor != null && trip.start > cursor) pushGap(cursor, trip.start);
    pieces.push({
      key: trip.id,
      start: trip.start,
      end: trip.end,
      label: `Рейс ${trip.originalItem?.id ?? idx + 1}`,
      // те же цвета, что у рейсов на шкале работы: соседние рейсы чередуются
      color: trip.color || tripColor(trip.originalItem || {}, idx),
    });
    cursor = cursor == null ? trip.end : Math.max(cursor, trip.end);
  });
  if (range && cursor != null && cursor < range.end) pushGap(cursor, range.end);
  if (!pieces.length && range) {
    pieces.push({ key: "none_all", none: true, start: range.start, end: range.end, label: "Нет рейсов" });
  }
  return pieces;
};

const TripProgressStrip = ({ playheadStore, trips, timeRange, onSeek, className, style }) => {
  const stripRef = useRef(null);
  const viewportRef = useRef(null);
  const lastRef = useRef({ pos: null, at: 0 });

  const range = useMemo(
    () => (timeRange ? { start: timeRange.start.getTime(), end: timeRange.end.getTime() } : null),
    [timeRange]
  );
  const pieces = useMemo(() => buildTripPieces(trips, range), [trips, range]);

  const update = useCallback(() => {
    const ms = playheadStore.get();
    const strip = stripRef.current;
    const viewport = viewportRef.current;
    if (!strip || !viewport || ms == null || !pieces.length) return;

    // Позиция в «рейсах» = номер куска + доля пройденного в нем
    let pos;
    if (ms <= pieces[0].start) pos = 0;
    else if (ms >= pieces[pieces.length - 1].end) pos = pieces.length;
    else {
      let i = indexAt(pieces, ms);
      if (i < 0) i = pieces.findIndex((p) => p.start > ms) - 1;
      const p = pieces[Math.max(0, i)];
      pos = Math.max(0, i) + Math.min(1, Math.max(0, (ms - p.start) / Math.max(1, p.end - p.start)));
    }

    // Между обновлениями времени лента доезжает плавно; большой скачок (перемотка) - сразу
    const last = lastRef.current;
    const now = performance.now();
    const dt = now - last.at;
    const smooth = last.pos != null && Math.abs(pos - last.pos) < 1 && dt < 1500;
    last.pos = pos;
    last.at = now;
    const x = viewport.clientWidth / 2 - pos * TRIP_PIECE_PX;
    strip.style.transition = smooth ? `transform ${Math.round(dt)}ms linear` : "none";
    strip.style.transform = `translateX(${x}px)`;
  }, [playheadStore, pieces]);

  useEffect(() => {
    lastRef.current = { pos: null, at: 0 };
    update();
    return playheadStore.subscribe(update);
  }, [playheadStore, update]);

  // Двойной клик: кусок под курсором ищем по фактическому положению ленты на экране
  // (во время плавного сдвига оно отличается от последней рассчитанной позиции)
  const handleDoubleClick = (e) => {
    const strip = stripRef.current;
    if (!strip || typeof onSeek !== "function") return;
    const idx = Math.floor((e.clientX - strip.getBoundingClientRect().left) / TRIP_PIECE_PX);
    const piece = pieces[idx];
    if (!piece) return;
    onSeek({ exactTime: piece.start, item: { label: piece.label } });
  };

  // При изменении ширины пересчитываем положение без анимации
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(() => {
      lastRef.current.pos = null;
      update();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [update]);

  return (
    <div
      ref={viewportRef}
      className={`position-relative ${className || ""}`}
      style={{ minWidth: "220px", height: "40px", overflow: "hidden", userSelect: "none", ...style }}
      title={onSeek ? "Прогресс рейса по кадру видео. Двойной клик по рейсу - перейти к его началу" : "Прогресс рейса по кадру видео"}
      onDoubleClick={handleDoubleClick}
    >
      <div
        ref={stripRef}
        className="d-flex position-absolute"
        style={{ left: 0, top: "8px", height: "30px", willChange: "transform" }}
      >
        {pieces.map((p) => (
          <div
            key={p.key}
            className="d-flex align-items-center justify-content-center fw-semibold text-nowrap"
            style={{
              cursor: onSeek ? "pointer" : "default",
              width: `${TRIP_PIECE_PX}px`,
              height: "100%",
              fontSize: "0.8rem",
              borderRight: "2px solid #fff",
              borderRadius: "6px",
              color: p.none ? "#6c757d" : "#fff",
              backgroundColor: p.none ? NO_TRIP_COLOR : p.color,
              backgroundImage: p.none
                ? "repeating-linear-gradient(-45deg, transparent, transparent 5px, rgba(0,0,0,0.06) 5px, rgba(0,0,0,0.06) 10px)"
                : undefined,
            }}
          >
            {p.label}
          </div>
        ))}
      </div>

      {/* Неподвижный маркер по центру - как маркер кадра на шкале */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "8px",
          bottom: 0,
          width: "2px",
          marginLeft: "-1px",
          backgroundColor: "#e55353",
          boxShadow: "0 0 6px rgba(229, 83, 83, 0.8)",
          pointerEvents: "none",
          zIndex: 2,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-6px",
            left: "-5px",
            width: "12px",
            height: "12px",
            backgroundColor: "#e55353",
            borderRadius: "50%",
            border: "2px solid white",
            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
          }}
        />
      </div>
    </div>
  );
};

TripProgressStrip.propTypes = {
  playheadStore: PropTypes.object.isRequired,
  trips: PropTypes.array.isRequired,
  timeRange: PropTypes.object,
  onSeek: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object,
};

TripProgressStrip.defaultProps = {
  timeRange: null,
  onSeek: null,
  className: "",
  style: undefined,
};

export default React.memo(TripProgressStrip);
